<template>
  <div>
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('organization_settings.webhooks') }}
      </p>
      <p class="mt-2 pa-0 grey--text text--darken-2">
        {{ $t('organization_settings.webhook_form_subtitle') }}
        <a :href="$t('common.developers_site')" target="__blank">
          {{ $t('organization_settings.webhook_form_developers_site') }}
        </a>
      </p>
    </div>

    <v-form class="mb-16" ref="form" @submit="handleSubmit">
      <v-text-field
        autocomplete="off"
        autofocus
        class="mb-2"
        data-private
        dense
        outlined
        :placeholder="$t('common.name')"
        v-model="form.name"
        data-tourid="webhooks-textField-name"
        :disabled="isLoading"
        :label="$t('common.name')"
      />

      <v-text-field
        autocomplete="off"
        autofocus
        class="mb-2"
        data-private
        dense
        outlined
        placeholder="https://example.com"
        prefix="POST"
        v-model="form.url"
        data-tourid="webhooks-textField-url"
        :disabled="isLoading"
        :label="$t('organization_settings.webhooks_url')"
        :rules="[validateURL]"
      />

      <v-text-field
        autocomplete="off"
        class="mb-2"
        data-private
        dense
        outlined
        type="password"
        v-model="form.secret"
        :disabled="isLoading"
        :label="$t('organization_settings.webhook_form_secret')"
      />

      <v-switch
        class="mb-10"
        persistent-hint
        v-model="form.enabled"
        :disabled="isLoading"
        :hint="$t('organization_settings.webhooks_active_subtitle')"
        :label="$t('common.active')"
      ></v-switch>

      <header class="grey--text text--darken-2 mb-n2">
        {{ $t('organization_settings.webhook_form_select_events') }}
      </header>
      <v-radio-group v-model="allEventsSelected">
        <v-radio
          data-tourid="webhooks-radio-sendMeEverything"
          :disabled="isLoading"
          :label="$t('organization_settings.webhook_form_every_event')"
          :value="true"
          @change="handleAllEvents"
        />
        <v-radio
          data-tourid="webhooks-radio-selectIndividual"
          :disabled="isLoading"
          :label="$t('organization_settings.webhook_form_indivial_event')"
          :value="false"
          @change="handleIndividualEvents"
        />
      </v-radio-group>

      <v-radio-group
        class="mt-n2"
        v-show="!allEventsSelected"
        v-model="form.events"
        :rules="[validateSelectedEvents]"
      >
        <v-row>
          <v-col cols="4">
            <CheckboxList
              :allowedEvents="['created', 'updated', 'deleted']"
              :entityName="'rev'"
              :initialSelectedEvents="form.events"
              :isLoading="isLoading"
              @change="handleChange"
            />
          </v-col>
          <v-col cols="4">
            <CheckboxList
              :allowedEvents="[
                'created',
                'updated',
                'deleted',
                'challengeRevLinked',
              ]"
              :entityName="'challenge'"
              :initialSelectedEvents="form.events"
              :isLoading="isLoading"
              @change="handleChange"
            />
          </v-col>
          <v-col cols="4">
            <CheckboxList
              :entityName="'account'"
              :isLoading="isLoading"
              :initialSelectedEvents="form.events"
              @change="handleChange"
            />
          </v-col>
          <v-col cols="4">
            <CheckboxList
              :entityName="'routine'"
              :isLoading="isLoading"
              :initialSelectedEvents="form.events"
              @change="handleChange"
            />
          </v-col>
          <v-col cols="4">
            <CheckboxList
              :entityName="'finding'"
              :isLoading="isLoading"
              :initialSelectedEvents="form.events"
              @change="handleChange"
            />
          </v-col>
          <v-col cols="4">
            <CheckboxList
              :entityName="'organization'"
              :isLoading="isLoading"
              :initialSelectedEvents="form.events"
              :allowedEvents="['updated']"
              @change="handleChange"
            />
          </v-col>
          <v-col cols="4" v-if="hasTasksActive">
            <CheckboxList
              :entityName="'task'"
              :isLoading="isLoading"
              :initialSelectedEvents="form.events"
              @change="handleChange"
            />
          </v-col>
        </v-row>
      </v-radio-group>

      <div class="mt-6 text-right">
        <Button
          class="me-2"
          color="grey"
          outlined
          size="large"
          data-tourid="webhooks-button-addWebhookConfirmation"
          :disabled="isLoading"
          :text="$t('common.go_back')"
          @click="$emit('cancel')"
        />

        <Button
          color="primary"
          size="large"
          type="submit"
          :text="
            isNew
              ? $t('organization_settings.webhooks_add_new')
              : $t('organization_settings.webhooks_update')
          "
          :loading="isLoading"
        />
      </div>
    </v-form>

    <LogList
      v-if="!isNew"
      :webhookId="this.webhook.id"
      :organizationId="organizationId"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import is from 'is_js'
import { mapActions } from 'vuex'
import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'

import LogList from './LogList.vue'
import Button from '@/components/UI/Button'
import CheckboxList from './CheckboxList.vue'

export default {
  name: 'WebhookForm',

  mixins: [mixin],

  components: {
    LogList,
    Button,
    CheckboxList,
  },

  props: {
    webhook: {
      type: Object,
      default() {
        return {}
      },
    },
    organizationId: {
      type: String,
      required: true,
    },
  },

  data() {
    const events = this.webhook?.events ?? ['all']

    return {
      isLoading: false,
      form: _.cloneDeep({ ...this.webhook, events }),
      eventSelectionType: events.includes('all') ? 'all' : 'individual',
    }
  },

  computed: {
    ...mapGetters(['organization']),

    hasTasksActive() {
      return this.isTasksEnabled
    },

    isTasksEnabled() {
      return this.organization.featuresEnabled__tasks
    },

    isNew() {
      return !this.webhook?.id
    },

    allEventsSelected: {
      get() {
        return this.form.events.includes('all')
      },
      set() {
        return
      },
    },
  },

  methods: {
    ...mapActions({
      createWebhook: 'createWebhook',
      updateWebhook: 'updateWebhook',
    }),
    validateURL(url = '') {
      const trimmedURL = url.trim()

      if (!trimmedURL) {
        return this.$t('organization_settings.webhooks_url_required')
      }

      if (!/^https/i.test(trimmedURL)) {
        return this.$t('organization_settings.webhooks_url_https_required')
      }

      if (!is.url(trimmedURL)) {
        return this.$t('organization_settings.webhooks_invalid_url')
      }

      return true
    },

    validateSelectedEvents(events) {
      if (_.isEmpty(events)) {
        return this.$t('organization_settings.webhooks_events_required')
      }

      return true
    },

    handleIndividualEvents() {
      this.form.events = []
    },

    handleAllEvents() {
      this.form.events = ['all']
    },

    handleSubmit(event) {
      event.preventDefault()

      const isValid = this.$refs.form.validate()
      if (!isValid) return

      if (this.isNew) return this.handleCreate()
      return this.handleUpdate()
    },

    handleChange(events) {
      this.form.events = events
    },

    async handleCreate() {
      this.isLoading = true

      try {
        await this.createWebhook({
          ...this.form,
          url: this.form.url.trim(),
          organizationId: this.organizationId,
        })
        this.$emit('saved')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async handleUpdate() {
      this.isLoading = true

      try {
        await this.updateWebhook({
          data: {
            ...this.form,
            url: this.form.url.trim(),
            organizationId: this.organizationId,
          },
          webhookId: this.webhook.id,
        })
        this.$emit('saved')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
